import { useState } from "react";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { useGenerateDirectSalesOpenReport } from "@/lib/api/directSales";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadFromS3 } from "@/lib/file";

interface OpenReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OpenReportDialog({ open, onOpenChange }: OpenReportDialogProps) {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [link, setLink] = useState<string | undefined>(undefined);
  const generateOpenReport = useGenerateDirectSalesOpenReport();

  const onGenerateReport = async () => {
    if (isGenerating) {
      return;
    }

    try {
      setIsGenerating(true);
      setLink(undefined);

      const result = await generateOpenReport();
      if (!result.success) {
        throw result;
      }

      setLink(result.data.link);

      // Download the file
      downloadFromS3(result.data.link, `direct-sales-open-report.xlsx`);

      toast({
        title: "Report Generated",
        description: "The open report has been generated successfully.",
      });
    } catch (error: any) {
      console.error("Failed to generate open report", error);
      toast({
        title: "Failed to generate open report",
        description: error?.message || "",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setLink(undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex flex-col ">
        <DialogHeader className="">
          <DialogTitle>Open Report</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Generate the full open direct sales report.
        </DialogDescription>
        
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          {isGenerating && (
            <p className="text-muted-foreground">Generating report...</p>
          )}
          
          {link && !isGenerating && (
            <div className="flex items-center flex-col sm:flex-row gap-2">
              <span className="text-green-600">Report generated!</span>
              <a 
                href={link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-500 hover:text-blue-700 underline"
              >
                Click here to download
              </a>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row justify-between items-center">
          <span className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={isGenerating}>
              Close
            </Button>
            <Button onClick={onGenerateReport} disabled={isGenerating}>
              Generate
            </Button>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
